import { Row } from "@tanstack/react-table";
import { ColumnDef } from "@tanstack/react-table";
import Tag from "@/components/Tag";
import { buildAvatarDetailsColumn, buildActionColumn, buildIconTextColumn } from "@/components/Table";
import { toFormat, isInFuture } from "@/lib/dateHelpers";
import { StudentInternship } from "./types";
import { getStatusTagColor } from "../_utils";
import IconButton from "@/components/IconButton";

type BuildColumnsProps = {
  isCompactLayout: boolean;
  handleViewFacility: (internship: StudentInternship) => void;
  handleEditInternship?: (internship: StudentInternship) => void;
  handleViewStudentInternships?: (internship: StudentInternship) => void;
  setMenu?: (menu: any) => void;
  actionId?: (rowId: string) => string;
  isAlumni?: boolean;
};

const getInternshipStatus = (internship: StudentInternship): "Completed" | "Not Started" | "In Progress" => {
  if (!internship.startedOn) return "Not Started";
  if (internship.startedOn && isInFuture(internship.startedOn)) return "Not Started";
  if (internship.endedOn && !internship.isActive) return "Completed";
  return "In Progress";
};

const futureDateWarningIcon = (supportingText: string) => ({
  name: "emergency_home" as const,
  size: 20,
  className: "text-extended-warning-brand-color",
  tooltip: {
    supportingText,
    variant: "plain" as const,
    color: "black" as const,
    menuCorner: "end-start" as const,
    anchorCorner: "start-start" as const
  }
});

export function buildColumns({
  isCompactLayout,
  handleViewFacility,
  handleEditInternship,
  handleViewStudentInternships,
  setMenu,
  actionId,
  isAlumni = false
}: BuildColumnsProps): ColumnDef<StudentInternship>[] {
  return [
    ...buildAvatarDetailsColumn<StudentInternship>({
      isCompactLayout,
      getTitle: (row) => row.student.displayName || `${row.student.firstName} ${row.student.lastName}`,
      getSubtitle: (row) => `PGA ID: ${row.student.pgaId}`,
      getPicture: (row) => row.student.profilePhoto,
      size: 200,
      minSize: 180
    }),
    {
      accessorKey: "facility",
      header: "Facility",
      cell: ({ row }: { row: Row<StudentInternship> }) => row.original.facility.name,
      enableSorting: false,
      size: 220,
      minSize: 180,
      maxSize: 280
    },
    {
      accessorKey: "state",
      header: "State",
      cell: ({ row }: { row: Row<StudentInternship> }) => row.original.facility.mainAddress?.state || "--",
      enableSorting: false,
      size: 80,
      minSize: 70,
      maxSize: 100
    },
    {
      accessorKey: "supervisor",
      header: "Supervisor",
      cell: ({ row }: { row: Row<StudentInternship> }) => {
        const supervisor = row.original.supervisor;
        const supervisorName = supervisor ? `${supervisor.firstName} ${supervisor.lastName}` : "--";
        const hasWarning = false;

        return (
          <>
            {supervisorName}
            {hasWarning && (
              <IconButton name="warning" iconClassName="text-warning" title="Supervisor has not read mentor guide" />
            )}
          </>
        );
      },
      enableSorting: false,
      size: 140,
      minSize: 120,
      maxSize: 180
    },
    buildIconTextColumn<StudentInternship>({
      accessorKey: "startedOn",
      header: "Start Date",
      enableSorting: true,
      getText: (row) => (row.startedOn ? toFormat(row.startedOn) : ""),
      getIcon: (row) =>
        row.startedOn && isInFuture(row.startedOn) ? futureDateWarningIcon("Start date is in the future") : null,
      size: 140,
      minSize: 120
    }),
    buildIconTextColumn<StudentInternship>({
      accessorKey: "endedOn",
      header: "End Date",
      enableSorting: false,
      getText: (row) => (row.endedOn ? toFormat(row.endedOn) : ""),
      getIcon: (row) => {
        const isFutureEnd = row.endedOn && isInFuture(row.endedOn);
        const isFutureStart = row.startedOn && isInFuture(row.startedOn);
        return isFutureEnd || (!row.endedOn && isFutureStart)
          ? futureDateWarningIcon("End date is in the future")
          : null;
      },
      size: 140,
      minSize: 120
    }),
    {
      accessorKey: "internshipCredits",
      header: "Months",
      cell: ({ row }: { row: Row<StudentInternship> }) =>
        row.original.internshipCredits ? row.original.internshipCredits.toFixed(1) : "--",
      enableSorting: false,
      size: 80,
      minSize: 70,
      maxSize: 100
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: { row: Row<StudentInternship> }) => {
        const status = getInternshipStatus(row.original);
        return (
          <Tag tagType={getStatusTagColor(status)} size="small">
            {status}
          </Tag>
        );
      },
      meta: {
        headerClassName: "hide-header-in-column"
      },
      enableSorting: false,
      size: 100,
      minSize: 90,
      maxSize: 120
    },
    buildActionColumn<StudentInternship>({
      getActions: (row) =>
        isAlumni
          ? []
          : [
              {
                text: "Edit Internship",
                onClick: () => handleEditInternship?.(row)
              },
              {
                text: "View Student",
                onClick: () => handleViewStudentInternships?.(row)
              }
            ],
      onViewClick: (row) => handleViewFacility(row),
      viewButtonTitle: "View Facility",
      actionId: actionId || (() => ""),
      setMenu: setMenu || (() => {})
    })
  ];
}
